import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';

import { filter, map, catchError } from 'rxjs/operators';

import { ErrorMessageService } from './errormessage.service';
import { PortalService } from './portal.service';
import { EnvironmentService } from './environment.service';

const dateRegex = /^(\d{4}-\d{2}-\d{2})(T\d{2}:\d{2}:\d{2})?([\+\-]\d{2}:\d{2})?$/;

// Render a stored UTC instant in the viewer's local timezone, matching the maintenance
// scheduler's live-preview banner format exactly: "MMM d, y, h:mm a ZZZ"
// (e.g. "Jul 2, 2026, 1:00 AM GMT+8") — hour/minute only, with the local zone abbreviation.
function formatWindow( d: Date ): string {
   return new Intl.DateTimeFormat( 'en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
      timeZoneName: 'short',
   } ).format( d );
}

interface MaintenanceMessage {
   begins: Date;
   expires: Date;
   windowStart: Date;
   windowEnd: Date;
   link: string;
   linkText: string;
   message: string;
   portals?: string[];
}

class MaintenanceMessage implements MaintenanceMessage {
   constructor( m: any ) {
      if ( m?.begins && dateRegex.test( m.begins )) { this.begins = new Date( m.begins ); }
      if ( m?.expires && dateRegex.test( m.expires )) { this.expires = new Date( m.expires ); }
      if ( m?.windowStart && dateRegex.test( m.windowStart )) { this.windowStart = new Date( m.windowStart ); }
      if ( m?.windowEnd && dateRegex.test( m.windowEnd )) { this.windowEnd = new Date( m.windowEnd ); }
      if ( m?.message ) {
         this.message = m.message;
         if ( this.windowStart ) {
            this.message = this.message.replace( /:windowStart/g, formatWindow( this.windowStart ) );
         }
         if ( this.windowEnd ) {
            this.message = this.message.replace( /:windowEnd/g, formatWindow( this.windowEnd ) );
         }
      }
   }
}

@Injectable({
   providedIn: 'root'
})
export class MaintenanceService {

   url: string;
   prefix = 'mm_';

   constructor(
      private http: HttpClient,
      public errors: ErrorMessageService,
      private portal: PortalService,
      private environmentService: EnvironmentService,
   ) { }
   
   check() {
      this.url = this.environmentService.maintenanceUrl;

      return this.http.get<MaintenanceMessage[]>( this.url ).pipe(
         filter( messages => !!messages.length ),
         map( messages => messages.filter( (msg) => {
            let ret = false; // By default the return is false.

            if (msg.portals == null || (msg.portals && msg.portals.length === 0)) { // This will check if the maintenance.json has portals defined
               // if not then this message will be on all portals.
               ret = true;
            } else if (msg.portals && msg.portals.length > 0) {
               // This will check if there's portals defined in the maintenance.json and not empty
               // and will check if the current portal is defined in the portals list.
               // This will check to true if the current portal is defined in the portals list.
               ret = msg.portals.includes(this.portal.current);
            }

            return ret;
         })),
         map( messages => messages.map( m => new MaintenanceMessage( m ))),
         catchError( _ => [] ),
      ).subscribe( messages => {
         messages.map(( message: MaintenanceMessage ) => {
            const hash = this.prefix + this.hash( JSON.stringify( message ));

            if ( message.windowStart < new Date() && message.windowEnd > new Date() ) {
               // current date is within maintenance window
               this.errors.add({
                  type: 'error',
                  code: 'maintenance',
                  message: message.message,
               });

               setTimeout( _ =>
                  this.errors.resetErrors(),
                  ( message.windowEnd.getTime() - new Date().getTime() )
               );
            } else if ( !sessionStorage.getItem( hash ) && message.expires > new Date()  && message.begins < new Date()) {
               // display notification in error bar, callback adds to session storage
               this.errors.add({
                  type: 'notification',
                  code: 'maintenance',
                  message: message.message,
                  callback: () => sessionStorage.setItem( hash, message.expires.getTime().toString() ),
               });
            }

            // console.log( 'maintenance hash', hash );
            // console.log( 'message', message );

            // clean up old exired acknoledgements
            Object.keys( sessionStorage ).map( key => {
               if ( key.slice( 0, this.prefix.length ) === this.prefix ) {
                  const date = new Date( +sessionStorage.getItem( key ));
                  if ( date < new Date() ) {
                     sessionStorage.removeItem( key );
                  }
               }
            });
         });
      });
   }

   // from cyrb53
   hash( str: string, seed = 0 ): string {
      /* tslint:disable no-bitwise number-literal-format */
      let h1 = 0xdeadbeef ^ seed;
      let h2 = 0x41c6ce57 ^ seed;
      for (let i = 0, ch: number; i < str.length; i++) {
         ch = str.charCodeAt(i);
         h1 = Math.imul(h1 ^ ch, 2654435761);
         h2 = Math.imul(h2 ^ ch, 1597334677);
      }
      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

      return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString();
   }
}
